import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LocalStorage, LocalStorageService } from 'ngx-webstorage';
import { ApiService } from '../../services/api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HeadingService } from '../../services/heading.service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { environment } from '../../../environments/environment';
import { LogoutService } from 'src/app/services/logout.service';
import { trigger, transition, style, animate, query, group, animateChild } from '@angular/animations';import { SamlAuthService } from 'src/app/services/saml-auth.service';
import { AuthService } from 'src/app/services/auth.service';

interface SamlProvider {
  IdpCatalogTenantKey: string;
  IdpCatalogName: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [
    trigger('formSwitch', [
      transition('login => changePassword', [
        query(':enter, :leave', style({ position: 'absolute', top: 0, left: 0, width: '100%' }), { optional: true }),
        group([
          query(':leave', [
            animate('400ms ease-in-out', style({ transform: 'translateX(-120%)', opacity: 0 }))
          ], { optional: true }),
          query(':enter', [
            style({ transform: 'translateX(120%)', opacity: 0 }),
            animate('400ms ease-in-out', style({ transform: 'translateX(0)', opacity: 1 }))
          ], { optional: true }),
        ]),
        query('@*', animateChild(), { optional: true }),
      ]),
      transition('changePassword => login', [
        query(':enter, :leave', style({ position: 'absolute', top: 0, left: 0, width: '100%' }), { optional: true }),
        group([
          query(':leave', [
            animate('400ms ease-in-out', style({ transform: 'translateX(120%)', opacity: 0 }))
          ], { optional: true }),
          query(':enter', [
            style({ transform: 'translateX(-120%)', opacity: 0 }),
            animate('400ms ease-in-out', style({ transform: 'translateX(0)', opacity: 1 }))
          ], { optional: true }),
        ]),
        query('@*', animateChild(), { optional: true }),
      ]),
    ])
  ]
})
export class LoginComponent implements OnInit {
    @ViewChild('recaptchaRef')
    recaptchaRef: RecaptchaComponent;
    needTokenConfirmation = false;
    mustChangePassword = false;
    loginForm: FormGroup;
    confirmationForm: FormGroup;
    submitted = false;
    languages = [];
    useRecaptcha = true;
    entraSignIn = false;
    samlProviders: SamlProvider[] = [];
    showSamlProviders = false;
    confirmationCodeError = false;
    sendingLoginRequest = false;
    loggedUserID: number;

    @LocalStorage() currentLang: string;
    constructor(private formBuilder: FormBuilder,
        private router: Router,
        private saml: SamlAuthService,
        private heading$: HeadingService,
        private localStorage: LocalStorageService,
        private translate$: TranslateService,
        private api$: ApiService,
        private authService: AuthService,
        private logoutService: LogoutService,
        private modalService: NgbModal) {
      this.useRecaptcha = environment.useRecaptcha;
      this.entraSignIn = environment.entraSignIn ?? false;
      this.heading$.title.next({ text: 'Checkpoint Cardio' });
    }

    ngOnInit() {
      this.buildForm();
      this.getLanguages();
      if (!this.authService.isAuthenticated()) {
        this.logoutService.deleteLocalFirebaseToken();
      }
      
    }

    private buildForm() {
      this.loginForm = this.formBuilder.group({
        username: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        language: [this.currentLang],
        recaptcha: ['', this.useRecaptcha ? Validators.required : null],
      });
      this.confirmationForm = this.formBuilder.group({
        confirmationCode: ['', [Validators.required]]
      });
      if (this.entraSignIn) {
        this.loadSamlProviders();
      }
    }



    loadSamlProviders(): void {
      this.api$.getSamlProviders().subscribe(data => {
        if (data.success === true) {
          this.samlProviders = data.data;
          console.log('providers', data.data);
        }
      });
    }


    getLanguages(): void {
      this.api$.languages()
        .subscribe(data => {
          if (data.success === true)  {
            this.languages = data.data.languages;
            if (!this.currentLang) {
              this.changeLanguage(data.data.defaultLang);
            }
          }
        });
    }

    // convenience getter for easy access to form fields
    get f() {
      return this.loginForm.controls;
    }

    onSubmit(content) {
      if (this.sendingLoginRequest == false) {
        this.submitted = true;

        // stop here if form is invalid
        if (this.loginForm.invalid) {
          return;
        }
        this.getLoginAut(content);
      }

    }

    changeLanguage(newLang) {
      this.translate$.use(newLang);
      this.translate$.setDefaultLang(newLang);
      if (newLang != this.currentLang) {
        this.authService.clearLocalStorageData();
        this.loginForm.patchValue({ language: newLang });
        this.currentLang = newLang;
      }
    }

    changeLanguageFix(newLang) {
      this.translate$.use(newLang);
      this.translate$.setDefaultLang(newLang);
      if (newLang != this.currentLang) {
        this.currentLang = newLang;
      }
    }

    async loginByIdentityProvider(key: string) {
      const user = await this.saml.loginVia(key);
      this.authService.loginUser(user);
      if (user.isTokenConfirmed == 1) {
        this.navigateAfterLogin();
      } else {
        this.needTokenConfirmation = true;
      }
    }


    // check if username and password are from database
    getLoginAut(content): void {
      this.sendingLoginRequest = true;
      this.confirmationCodeError = false;
      this.api$.getLoginAut(this.loginForm.value.username, this.loginForm.value.password, this.loginForm.value.recaptcha)
        .subscribe(data => {
          this.sendingLoginRequest = false;
          this.submitted = false;
          if (data.success === true) {
            this.localStorage.store('token', data.data.userToken.token);
            this.localStorage.store('loggedUser', data.data);
            if (data.data.UserBookingUid) {
              this.localStorage.store('userUid', data.data.UserBookingUid);
            }
            this.localStorage.store('selectedMedCenter', 0);
            if (data.data.UserLang) {
              this.changeLanguageFix(data.data.UserLang);
            }
            if (data.data.isTokenConfirmed == 1) {
              // Check if user must change password on first login
              if (data.data.UserMustChangePassword === true || data.data.UserMustChangePassword === 1) {
                this.loggedUserID = data.data.UserID;
                this.mustChangePassword = true;
              } else {
                this.navigateAfterLogin();
              }
            } else {
              this.needTokenConfirmation = true;
            }
          } else {
            this.open(content);
            if (this.recaptchaRef) {
              this.recaptchaRef.reset();
            }
          }
        });
    }

    navigateAfterLogin() {

      const redirect = this.localStorage.retrieve('redirectUrl');
      if (redirect) {
        this.localStorage.clear('redirectUrl');
        this.router.navigate([redirect]).then();
      } else  {
        this.router.navigate(['/dashboard']).then();
      }

    }

    // open dialog for mismatch on username and password
    open(content) {
      this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
    }

    public addTokenLog(message: string, token: string | null) {
      console.log('token', token);
    }

    onPasswordChanged() {
      const loggedUser = this.localStorage.retrieve('loggedUser');
      if (loggedUser) {
        loggedUser.UserMustChangePassword = 0;
        this.localStorage.store('loggedUser', loggedUser);
      }
      const redirect = this.localStorage.retrieve('redirectUrl');
      if (redirect) {
        this.localStorage.clear('redirectUrl');
        this.router.navigate([redirect]).then();
      } else {
        this.router.navigate(['/dashboard']).then();
      }
    }

    public sendConfirmationCode() {
      this.confirmationCodeError = false;
      this.api$.confirmLoginToken(this.confirmationForm.value.confirmationCode)
        .subscribe(data => {
          if (data.success === true) {
            if (data.data == 1) {
              this.navigateAfterLogin();
            } else {
              this.router.navigate(['/login']).then();
            }
          } else if (data.data.message == 'Confirmation code has expired') {
            this.needTokenConfirmation = false;
            this.loginForm.patchValue({
              username: '',
              password: '',
            });
            this.confirmationForm.patchValue({
              confirmationCode: '',
            });
            this.confirmationCodeError = true;
          } else {
            this.confirmationCodeError = true;
          }
        });
    }
}
